import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Button, Heading } from '@adobe/react-spectrum';
import { EditorState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';

import actionWebInvoke from '../../utils/actionCallUtils';
import getHeaders from '../../utils/headers';
import actions from '../../config.json';
import OBJCODE from '../../constants';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import PageSection from '../../components/Layout/Content/PageSection';
import DateTime from '../../components/Forms/DateTime';
import Priority from './Lists/Priority';
import QueueTopic from './QueueTopic';
import Tags from '../Brief/Tags';
import Inspiration from '../Brief/Inspiration';

import {
  Wrap,
  Overview,
  LaunchDateTime,
  CompletionDateTime,
  ExistingBriefs,
  Actions,
  Submit,
  EditorWrapper,
  EditorHeaderText,
  RightHandsideAligner,
} from '../Brief/styles';
import RequestProgress from '../../components/RequestProgress';
import { getEstDateTime } from '../../utils/utils';

const Request = ({ ims, user }) => {
  const [requestProjectIdValue, setRequestProjectIdValue] = useState('');
  const [configurations, setConfigurations] = useState({
    isLoading: true,
    elements: [],
  });

  async function getConfigurations() {
    setConfigurations({ ...configurations, isLoading: true });
    const headers = getHeaders(ims);
    const actionResponse = await actionWebInvoke(
      actions['aio-lib-state-read'],
      headers,
      'key',
    );
    setConfigurations({
      ...configurations,
      configurations: actionResponse,
      isLoading: false,
    });
    setRequestProjectIdValue(actionResponse.requestProjectId);
    requestProjectIdValue = actionResponse.requestProjectId;
    return console.log('fetching user configuration', actionResponse);
  }
  useEffect(() => {
    getConfigurations();
  }, [ims]);

  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [launchDate, setLaunchDate] = useState(new Date());
  const [launchTime, setLaunchTime] = useState(new Date());
  const [completionDate, setCompletionDate] = useState(new Date());
  const [completionTime, setCompletionTime] = useState(new Date());

  const [tags, setTags] = useState([]);
  const [inspirationLinks, setInspirationLinks] = useState([]);
  const [priority, setPriority] = useState({
    isLoading: true,
    elements: [],
    selected: '',
  });

  const [queueTopic, setQueueTopic] = useState({
    isLoading: true,
    elements: [],
  });

  const [templates, setTemplates] = useState({
    isLoading: true,
    elements: [],
    selected: '',
  });

  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(),
  );

  const [convertedContent, setConvertedContent] = useState(null);

  const handleSubmit = async () => {
    setLoading(true);

    const queryParams = {
      name: title,
      priority: priority.selected,
      ID: user,
      queueTopicID: queueTopic.selected,
      projectID: requestProjectIdValue,
      'DE:AssetTags': tags.join(','),
      'DE:AssetLinks': inspirationLinks.join(','),
      'DE:AnticipatedLaunchDate': `${format(launchDate, 'yyyy-MM-dd')}T${format(
        launchTime,
        'HH:mm:ss',
      )}`,
      'DE:AssessMyRequestBy': `${format(completionDate, 'yyyy-MM-dd')}T${format(
        completionTime,
        'HH:mm:ss',
      )}`,
      method: 'post',
    };

    const actionResponse = await actionWebInvoke(
      actions['workfront-action'],
      {},
      { objCode: OBJCODE.OPTASK, queryParams },
    );

    console.log('actionResponse', actionResponse);
    await actionWebInvoke(
      actions['workfront-action'],
      {},
      {
        objCode: OBJCODE.OPTASK,
        objID: actionResponse.ID,
        bodyData: `{"DE:ProjDesc":${JSON.stringify(convertedContent)}}`,
      },
    );
    const requestURL = `${actionResponse.workfrontEndpoint}/issue/${actionResponse.ID}/overview`;
    window.open(requestURL, '_blank');
    setLoading(true);
  };

  const convertContentToHTML = () => {
    const currentContentAsHTML = JSON.stringify(
      convertToRaw(editorState.getCurrentContent()),
    );
    setConvertedContent(currentContentAsHTML);
  };

  const handleEditorChange = state => {
    setEditorState(state);
    convertContentToHTML();
  };

  return (
    <Wrap>
      <div>
        <PageSection title="Request Brief">
          {loading}
          <QueueTopic
            queueTopic={queueTopic}
            requestProjectIdValue={requestProjectIdValue}
            setQueueTopic={setQueueTopic}
          />
          <Overview
            label="Project Overview"
            value={title}
            onChange={setTitle}
            placeholder="Title that describes project"
            width="750px"
            divider
          />
          <LaunchDateTime>
            <DateTime
              label="When does it needs to be live"
              dateFormat="MM/dd/YYY"
              onChange={date => {
                console.log('date: ', date);
                setLaunchDate(getEstDateTime(date));
              }}
              minDate={new Date()}
              showDisabledMonthNavigation
              value={launchDate}
              selected={launchDate}
              placeholderText="MM/dd/YYY"
            />
            <DateTime
              label=""
              selected={launchTime}
              onChange={date => setLaunchTime(getEstDateTime(date))}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              timeCaption="Time"
              dateFormat="h:mm aa"
            />
          </LaunchDateTime>
          <CompletionDateTime>
            <DateTime
              label="Review request by"
              dateFormat="MM/dd/YYY"
              onChange={date => {
                setCompletionDate(date);
              }}
              value={completionDate}
              selected={completionDate}
              placeholderText="MM/dd/YYY"
            />
            <DateTime
              label=""
              selected={completionTime}
              onChange={date => setCompletionTime(date)}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              timeCaption="Time"
              dateFormat="h:mm aa"
            />
          </CompletionDateTime>
          <EditorWrapper>
            <EditorHeaderText>Project Challenge</EditorHeaderText>
            <Editor
              toolbar={{
                options: ['inline', 'list', 'blockType', 'link'],
                inline: {
                  inDropdown: false,
                  options: ['bold', 'italic', 'underline'],
                },
                blockType: {
                  inDropdown: false,
                  options: ['Blockquote'],
                },
                list: {
                  options: ['unordered', 'ordered'],
                },
                link: {
                  options: ['link'],
                },
              }}
              placeholder="Description of the project"
              wrapperClassName="wrapper-class"
              editorClassName="editor-class"
              toolbarClassName="toolbar-class"
              editorState={editorState}
              onEditorStateChange={handleEditorChange}
            />
          </EditorWrapper>
          <Tags tags={tags} setTags={setTags} />
          <Priority priority={priority} setPriority={setPriority} />
          <Inspiration
            inspirationLinks={inspirationLinks}
            setInspirationLinks={setInspirationLinks}
          />
          <Actions>
            <Submit variant="cta" onPress={handleSubmit}>
              Submit
            </Submit>
          </Actions>
        </PageSection>
      </div>
      <RightHandsideAligner>
        <ExistingBriefs value="Make Project Brief">
          <Heading level="4" margin="10px 20px 20px">
            Or start a project from a template
          </Heading>
          <Button top="10px" elementType="a" href="#/brief" variant="primary">
            Create Project Brief
          </Button>
        </ExistingBriefs>
        <RequestProgress user={user} />
      </RightHandsideAligner>
    </Wrap>
  );
};

Request.propTypes = {
  user: PropTypes.string,
};

Request.defaultProps = {
  user: '',
};

export default Request;
